import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Paper,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import type { StudentRow } from '../../types';

interface StudentDetailDrawerProps {
  student: StudentRow | null;
  open: boolean;
  onClose: () => void;
  onEdit: (student: StudentRow) => void;
  onBlock: (student: StudentRow) => void;
  canEdit: boolean;
  blockLoading: boolean;
  pickupOptions: { value: string; label: string }[];
}

const StudentDetailDrawer: React.FC<StudentDetailDrawerProps> = ({ student, open, onClose, onEdit, onBlock, canEdit, blockLoading, pickupOptions }) => {
  const getPickupLabel = (value: string) => pickupOptions.find((o) => o.value === value)?.label ?? value;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
    >
      {student && (
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>{student.name}</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>Student ID: {student.studentId}</Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Class / Section</Typography>
            <Typography variant="body1">{student.class} {student.section ? `– ${student.section}` : ''}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Dispersal</Typography>
            <Chip label={getPickupLabel(student.pickupMethod ?? '')} size="small" variant="outlined" />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            {(student.isDispersalDisabled ?? student.isDismissalDisabled) ? <Chip label="Blocked" size="small" color="warning" /> : <Chip label="Active" size="small" color="success" />}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Guardians</Typography>
          {(student.guardians?.length ?? 0) > 0 ? (
            <Box sx={{ mb: 2 }}>
              {student.guardians?.map((g, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Typography variant="body2"><strong>{g.name}</strong> · {g.mobile}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.relation}
                    {g.isPrimaryContact ? ' · Primary contact' : ''}
                    {(g.canRequestDispersal ?? g.canRequestDismissal) !== false ? ' · Can request dispersal' : ' · No dispersal permission'}
                  </Typography>
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No guardians. {(student.motherName || student.fatherName) ? `(Legacy: ${[student.motherName, student.fatherName].filter(Boolean).join(', ')})` : ''}</Typography>
          )}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>Parent pickup delegates</Typography>
          {(student.parentDelegates?.length ?? 0) > 0 ? (
            <Box sx={{ mb: 2 }}>
              {student.parentDelegates?.map((d, i) => (
                <Paper key={d.userId?._id ?? i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Typography variant="body2">
                    <strong>{d.userId?.name ?? '—'}</strong>
                    {d.userId?.mobile ? ` · ${d.userId.mobile}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(d.canViewStudent !== false) ? 'Can view student' : 'No app view'}
                    {' · '}
                    {(d.canDispersal !== false) ? 'Pickup / dispersal' : 'No pickup actions'}
                  </Typography>
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>None (primary parent can add delegates in the parent app).</Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {canEdit && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => onEdit(student)}
              >
                Edit
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color={student.isDismissalDisabled ? 'success' : 'warning'}
                startIcon={<BlockIcon />}
                onClick={() => onBlock(student)}
                disabled={blockLoading}
              >
                {(student.isDispersalDisabled ?? student.isDismissalDisabled) ? 'Unblock' : 'Block'}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default StudentDetailDrawer;
